/**
 * **이 기기에 저장된 것 전부 지우기** — 처음 온 것처럼 되돌린다.
 *
 * ## 왜 필요한가
 * 이 서비스는 가입이 없어서(불변식 1) 아이가 만든 히어로·모은 별·랠리 완주가 전부
 * **이 기기 안에만** 있다. 그래서 다시 처음부터 해 보고 싶을 때 지울 방법이 없었다 —
 * 개발 중에는 개발자 도구를 열어야 했고, 보호자는 아예 방법이 없었다.
 *
 * 랠리 완주는 서버(익명 코드)에도 기록되지만, **이 기기의 히어로 코드를 버리면**
 * 그 기록과의 연결이 끊긴다. 서버 기록 자체는 비식별 집계용으로 남는다(선착순 순위 등).
 *
 * ## 목록을 손으로 적지 않는다
 * 키를 하나 빠뜨리면 "지웠는데 뭔가 남아 있는" 상태가 된다 — 무엇이 남았는지 알 방법이 없다.
 * 그래서 **접두사로 쓸어 담는다**(`gc_`). 새 기능이 키를 늘려도 저절로 포함된다.
 */

import { wipeFamilyVoice } from "./familyVoice";

/** 이 서비스가 쓰는 저장 키 접두사. 다른 사이트 값은 건드리지 않는다. */
export const LOCAL_PREFIX = "gc_";

export interface ResetResult {
  /** 지운 키 이름들(디버그·확인용). */
  removed: string[];
  /**
   * 🔴 **지우려다 막혔나.**
   *
   * 사생활 보호 모드처럼 저장소를 못 쓰는 기기가 있다. 그때 조용히 넘기면 보호자는
   * [처음부터 다시 하기]를 눌렀는데 **아무것도 안 지워진 화면**을 다시 본다 —
   * 눌렀는데 그대로면 「고장」이 아니라 「안 지워지는구나」를 알아야 다음 수를 쓴다.
   */
  failed: boolean;
}

/** 저장소 하나에서 접두사로 시작하는 키를 모두 지운다. 실패해도 throw 하지 않는다. */
function wipe(store: Storage | undefined, removed: string[], fail: { hit: boolean }): void {
  if (!store) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k?.startsWith(LOCAL_PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      store.removeItem(k);
      removed.push(k);
    }
  } catch {
    // 🔴 「지울 게 없는 것과 같다」가 아니다 — 남아 있는데 못 지운 것이다. 화면이 말해야 한다
    fail.hit = true;
  }
}

/**
 * 이 기기의 저장값을 전부 지운다(히어로·별·랠리 완주·환영 팝업·환경설정).
 * 되돌릴 수 없다 — 호출부가 먼저 확인을 받는다.
 */
export function resetLocalData(): ResetResult {
  const removed: string[] = [];
  const fail = { hit: false };
  if (typeof window === "undefined") return { removed, failed: false };
  wipe(window.localStorage, removed, fail);
  wipe(window.sessionStorage, removed, fail);
  /**
   * 📥 담아 둔 책도 — 목록(gc_kept_books_v1)만 지우면 사본이 남는데, 보호자 화면은 목록으로만 지울 수 있어
   * **다시는 못 지우는 사본**이 된다. 이름은 lib/keepBook·sw.js 와 같다(swKept.test 가 셋을 맞춘다).
   * 끝나면 워커에게 알린다 — 담은 책이 없으면 책 길을 다시 안 가로챈다.
   */
  if (typeof caches !== "undefined") {
    void caches
      .delete("greg-books")
      .then(() => navigator.serviceWorker?.controller?.postMessage("kept-changed"))
      // 이 결과는 위 failed 보다 늦게 온다(화면은 이미 답했다) — 삼키지 않고 남긴다. 사본은 다음 「담아 두기」·「지우기」로 덮인다
      .catch((err: unknown) => console.warn("담아 둔 책 사본을 못 지웠어요", err));
  }
  /**
   * 🎙 보호자 녹음도 — IndexedDB 라 위 접두사 쓸기에 안 걸린다. 빠지면 **다시는 못 지우는 목소리**가 남는다
   * (lib/familyVoice · 리서치 최종안 ⑦). 늦게 끝나는 것은 담아 둔 책과 같다 — 삼키지 않고 남긴다.
   */
  void wipeFamilyVoice().catch((err: unknown) => console.warn("보호자 녹음을 못 지웠어요", err));
  return { removed, failed: fail.hit };
}
